import React, { useState, useRef, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";
import ImageCropper from "./imageCropper";
import { dentistFields, formFields, getAllDays } from "./clinic_function/addClinic_function";
import { toggleState } from "../../specialMethod/toggleState";

// Helper functions for localStorage
const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

const saveClinics = (clinics) => {
  localStorage.setItem("clinics", JSON.stringify(clinics));
};

const getDoctorData = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

const EditClinic = ({ clinic, reset }) => {
  const [state, setState] = useState({
    loadingSpinner: false,
    image: "",
    currentPage: false,
  });

  const inputRef = useRef();
  const doctorData = getDoctorData();
  const isDentist = doctorData?.specialization?.[0]?.toLowerCase() === "dentist";

  // Form validation schema
  const validationSchema = Yup.object().shape({
    clinicName: Yup.string().required("Enter clinic name"),
    location: Yup.string().required("Enter location"),
    startTime: Yup.string().required("Enter start time"),
    endTime: Yup.string().required("Enter end time"),
    incharge: Yup.string().required("Enter incharge name"),
    clinicContact: Yup.string()
      .matches(/^\d{10}$/, "Mobile number must be 10 digits")
      .required("Enter mobile number"),
    clinicNewFees: Yup.number()
      .nullable()
      .notRequired(),
    clinicOldFees: Yup.number()
      .nullable()
      .notRequired(),
    days: Yup.array().min(1, "Select at least one day").required("Please add days"),
    // Dentist fields are optional, but we keep them as numbers
    ...(isDentist
      ? {
          generalDentistry: Yup.number().nullable(),
          cosmeticDentistry: Yup.number().nullable(),
          dentalCheckUp: Yup.number().nullable(),
          restorationAndFilling: Yup.number().nullable(),
          crownsAndBridges: Yup.number().nullable(),
          fractureAndTrauma: Yup.number().nullable(),
          vaneersAndLaminates: Yup.number().nullable(),
          extraction: Yup.number().nullable(),
          oralAndMaxillofacialSurgery: Yup.number().nullable(),
          orthodonticsTreatment: Yup.number().nullable(),
          childDentistry: Yup.number().nullable(),
          implant: Yup.number().nullable(),
          scalingAndTeethCleaning: Yup.number().nullable(),
          rootCanalTreatment: Yup.number().nullable(),
        }
      : {}),
  });

  // Initialize Formik with clinic data
  const formik = useFormik({
    initialValues: {
      clinicName: clinic?.clinicName || "",
      location: clinic?.location || "",
      startTime: clinic?.startTime || "",
      endTime: clinic?.endTime || "",
      incharge: clinic?.incharge || "",
      clinicContact: clinic?.clinicContact || "",
      clinicNewFees: clinic?.clinicNewFees || 0,
      clinicOldFees: clinic?.clinicOldFees || 0,
      days: clinic?.days || [],
      dropdownOpen: false,
      generalDentistry: clinic?.generalDentistry || 0,
      cosmeticDentistry: clinic?.cosmeticDentistry || 0,
      dentalCheckUp: clinic?.dentalCheckUp || 0,
      restorationAndFilling: clinic?.restorationAndFilling || 0,
      crownsAndBridges: clinic?.crownsAndBridges || 0,
      fractureAndTrauma: clinic?.fractureAndTrauma || 0,
      vaneersAndLaminates: clinic?.vaneersAndLaminates || 0,
      extraction: clinic?.extraction || 0,
      oralAndMaxillofacialSurgery: clinic?.oralAndMaxillofacialSurgery || 0,
      orthodonticsTreatment: clinic?.orthodonticsTreatment || 0,
      childDentistry: clinic?.childDentistry || 0,
      implant: clinic?.implant || 0,
      scalingAndTeethCleaning: clinic?.scalingAndTeethCleaning || 0,
      rootCanalTreatment: clinic?.rootCanalTreatment || 0,
    },
    validationSchema,
    onSubmit: (values) => handleEditFunction(values),
  });

  // Toggle day selection
  const toggleDaySelection = (day) => {
    const normalizedDay = day.toUpperCase();
    const selectedDays = formik.values.days;
    const updatedDays = selectedDays.includes(normalizedDay)
      ? selectedDays.filter((d) => d !== normalizedDay)
      : [...selectedDays, normalizedDay];
    formik.setFieldValue("days", updatedDays);
  };

  // Handle image selection
  const handleFileChange = (event) => {
    if (event.target.files?.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        toggleState("image", reader.result, setState);
        toggleState("currentPage", true, setState);
      };
      reader.readAsDataURL(file);
    }
  };

  // Crop handlers
  const onCropDone = useCallback((croppedImage) => {
    toggleState("image", croppedImage, setState);
    toggleState("currentPage", false, setState);
  }, []);

  const onCropCancel = useCallback(() => {
    toggleState("image", "", setState);
    toggleState("currentPage", false, setState);
  }, []);

  // Delete uploaded image (optional)
  const deletePrescriptionImg = useCallback(() => {
    toggleState("image", "", setState);
  }, []);

  // Main edit function
  const handleEditFunction = useCallback(
    (values) => {
      setState((prev) => ({ ...prev, loadingSpinner: true }));

      // Get existing clinics
      const clinics = getClinics();
      const index = clinics.findIndex((c) => c.id === clinic.id);

      if (index === -1) {
        toast.error("Clinic not found.");
        setState((prev) => ({ ...prev, loadingSpinner: false }));
        return;
      }

      // Prepare updated clinic data
      const updatedClinic = {
        ...clinics[index],
        ...values,
        image: state.image || clinics[index].image || "",
        updatedAt: new Date().toISOString(),
      };

      // Save updated list
      clinics[index] = updatedClinic;
      saveClinics(clinics);

      toast.success("Clinic updated successfully!");

      setState((prev) => ({ ...prev, loadingSpinner: false }));
      reset(); // close modal
    },
    [clinic.id, state.image, reset]
  );

  // Dropdown toggle for days
  const handleToggleDropDown = useCallback(() => {
    formik.setFieldValue("dropdownOpen", !formik.values.dropdownOpen);
  }, [formik]);

  return (
    <>
      <div
        id="login-popup"
        tabIndex="-1"
        className="bg-black/50 fixed top-0 right-0 left-0 z-50 h-full flex items-center justify-center"
      >
        <div
          className={`relative w-full p-4 h-full ${
            isDentist ? "max-w-5xl" : "max-w-md"
          } md:h-auto`}
        >
          <div className="h-[80%] md:h-[92%] overflow-y-auto">
            <div className="bg-white rounded-lg shadow relative">
              {/* Close Button */}
              <button
                type="button"
                className="absolute top-3 right-2.5 text-gray-400 hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5"
                onClick={reset}
              >
                <svg
                  aria-hidden="true"
                  className="w-5 h-5"
                  fill="#c6c7c7"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="sr-only">Close popup</span>
              </button>

              {/* Form Content */}
              <div className="p-5">
                <p className="text-center mb-4 text-2xl font-semibold leading-5 text-slate-900">
                  Clinic Details
                </p>
                <hr className="h-[1px] bg-gray-600 my-1 mb-3" />
                <form onSubmit={formik.handleSubmit}>
                  {isDentist ? (
                    <>
                      <div className="grid grid-cols-3 gap-6">
                        {/* Image Upload */}
                        <div className="col-span-3 sm:col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Prescription Photo
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            ref={inputRef}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                          <button
                            type="button"
                            className="text-blue-600"
                            onClick={() => inputRef.current?.click()}
                          >
                            {state.image ? "Image Selected" : "Choose Image"}
                          </button>
                          {state.image && (
                            <span
                              className="pl-4 text-red-500 cursor-pointer"
                              onClick={deletePrescriptionImg}
                            >
                              <svg
                                className="w-5 h-5 text-red-500 inline"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9 2a1 1 0 011 1v1h6a1 1 0 110 2h-1v11a2 2 0 01-2 2H7a2 2 0 01-2-2V6H4a1 1 0 110-2h6V3a1 1 0 011-1zM7 6v11h6V6H7zm2-3v1h2V3H9z" />
                              </svg>
                            </span>
                          )}
                        </div>

                        {/* Common fields (excluding clinicNewFees and clinicOldFees for dentist) */}
                        {formFields
                          .filter((f) => f.name !== "clinicNewFees" && f.name !== "clinicOldFees")
                          .map(({ name, label, type, placeholder }) => (
                            <div key={name} className="col-span-3 sm:col-span-1">
                              <label className="block text-sm font-medium text-gray-700">
                                {label}
                              </label>
                              <input
                                type={type}
                                name={name}
                                placeholder={placeholder}
                                value={formik.values[name]}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="w-full px-4 py-2 border rounded-lg focus:border-blue-500"
                              />
                              {formik.touched[name] && formik.errors[name] && (
                                <p className="text-red-500 text-sm">{formik.errors[name]}</p>
                              )}
                            </div>
                          ))}

                        {/* Days Selection */}
                        <div className="col-span-3 sm:col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Select Days
                          </label>
                          <button
                            type="button"
                            className="text-gray-700 bg-white border border-gray-400 rounded-lg px-6 py-2"
                            onClick={handleToggleDropDown}
                          >
                            {formik.values.days.length} Days
                          </button>
                          {formik.values.dropdownOpen && (
                            <ul className="mt-2 p-3 border rounded bg-white">
                              {getAllDays.map((day) => (
                                <li key={day}>
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={formik.values.days.includes(day.toUpperCase())}
                                      onChange={() => toggleDaySelection(day)}
                                    />
                                    <span>{day}</span>
                                  </label>
                                </li>
                              ))}
                            </ul>
                          )}
                          {formik.touched.days && formik.errors.days && (
                            <p className="text-red-500 text-sm">{formik.errors.days}</p>
                          )}
                        </div>
                      </div>

                      {/* Dentist Service & Charges */}
                      <div className="font-semibold my-1">Service & Charges</div>
                      <div className="grid grid-cols-3 gap-6 p-2 rounded-lg my-2">
                        {dentistFields.map(({ name, label, type, placeholder }) => (
                          <div key={name} className="flex border border-gray-200 py-2">
                            <span className="text-gray-500 ml-1">{label}</span>
                            <span className="ml-auto text-gray-900 border-l border-gray-800">
                              <input
                                type={type}
                                name={name}
                                placeholder={placeholder || "00"}
                                value={formik.values[name]}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="w-10 ml-2"
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-6">
                      {/* File Input */}
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prescription Photo
                        </label>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            ref={inputRef}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                          <p className="text-blue-600 flex items-center">
                            <span onClick={() => inputRef.current?.click()} className="cursor-pointer">
                              {state.image ? "Image Already Chosen" : "Choose Image"}
                            </span>
                            {state.image && (
                              <span
                                className="pl-4 text-red-500 cursor-pointer"
                                onClick={deletePrescriptionImg}
                              >
                                <svg
                                  className="w-6 h-6 text-red-500 fill-current"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9 2a1 1 0 011 1v1h6a1 1 0 110 2h-1v11a2 2 0 01-2 2H7a2 2 0 01-2-2V6H4a1 1 0 110-2h6V3a1 1 0 011-1zM7 6v11h6V6H7zm2-3v1h2V3H9z" />
                                </svg>
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Dynamic Input Fields */}
                      {formFields.map(({ label, name, type, placeholder }) => (
                        <div className="col-span-2 sm:col-span-1" key={name}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {label}
                          </label>
                          <input
                            type={type}
                            name={name}
                            value={formik.values[name]}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder={placeholder}
                            className="w-full py-3 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                          />
                          {formik.touched[name] && formik.errors[name] && (
                            <p className="text-red-500">{formik.errors[name]}</p>
                          )}
                        </div>
                      ))}

                      {/* Days Selector */}
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Days
                        </label>
                        <button
                          type="button"
                          className="text-gray-700 bg-white border border-gray-400 px-4 py-2 rounded-lg focus:ring focus:outline-none"
                          onClick={handleToggleDropDown}
                        >
                          {formik.values.days.length} Days Selected
                        </button>
                        {formik.values.dropdownOpen && (
                          <ul className="absolute mt-2 bg-white shadow-lg rounded-lg p-3 w-48">
                            {getAllDays.map((day) => (
                              <li key={day} className="p-2 flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formik.values.days.includes(day.toUpperCase())}
                                  onChange={() => toggleDaySelection(day)}
                                  className="mr-2"
                                />
                                {day}
                              </li>
                            ))}
                          </ul>
                        )}
                        {formik.touched.days && formik.errors.days && (
                          <p className="text-red-500">{formik.errors.days}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="mt-8 w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600"
                  >
                    Submit
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Cropper */}
      {state.currentPage && (
        <ImageCropper
          image={state.image}
          onCropDone={onCropDone}
          onCropCancel={onCropCancel}
        />
      )}

      {/* Spinner */}
      {state.loadingSpinner && <Spinner />}
    </>
  );
};

export default EditClinic;